import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { ScanResult } from '../../utils/types';
import TrackerTable from '../TrackerTable';
import CookieTable from '../CookieTable';
import NetworkTrace from '../NetworkTrace';
import PagesAssessed from './PagesAssessed';
import { T } from '../../designTokens';

interface ProofAppendixProps {
  result: ScanResult;
  /** Persistent report id, forwarded to PagesAssessed for its screenshots. */
  reportId?: string | null;
}

export interface ProofAppendixHandle {
  openTo: (section: string, filter?: string) => void;
}

const ProofAppendix = forwardRef<ProofAppendixHandle, ProofAppendixProps>(({ result, reportId }, ref) => {
  const [openSection, setOpenSection] = useState<string | null>(null);
  const [sectionFilter, setSectionFilter] = useState<string>('');

  // Expose openTo for deep linking from per-issue evidence chips
  useImperativeHandle(ref, () => ({
    openTo(section: string, filter?: string) {
      setOpenSection(section);
      setSectionFilter(filter ?? '');
      setTimeout(() => {
        const el = document.getElementById(`appendix-${section}`);
        if (el) {
          const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
          el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });
        }
      }, 100);
    }
  }));

  const renderTrackerSummary = () => {
    const trackers = result.sections?.trackerDetection?.details?.trackers ?? [];
    if (trackers.length === 0) return null;

    const summary = result.sections?.trackerDetection?.details?.summary ?? {
      totalThirdParty: 0, marketing: 0, analytics: 0, socialMedia: 0, functional: 0, unknown: 0,
    };

    const isOpen = openSection === 'trackers';

    return (
      <div id="appendix-trackers" style={{ marginBottom: 32 }}>
        <div className="report-section-header">
          <span className="report-section-header__num">03</span>
          <h2 className="report-section-header__title">Trackers</h2>
          <span className="report-section-header__meta">{summary.totalThirdParty} third-party</span>
        </div>

        <div className="tracker-summary__chips">
          {[
            { label: 'Marketing', val: summary.marketing, color: T.error },
            { label: 'Analytics', val: summary.analytics, color: T.info },
            { label: 'Social', val: summary.socialMedia, color: T.warning },
            { label: 'Functional', val: summary.functional, color: T.success },
            { label: 'Unknown', val: summary.unknown, color: T.gray500 },
          ]
            .filter((x) => x.val > 0)
            .map(({ label, val, color }) => (
              <div key={label} className="tracker-chip">
                <span className="tracker-chip__dot" style={{ backgroundColor: color }} />
                <span className="tracker-chip__value">{val}</span>
                <span className="tracker-chip__label">{label}</span>
              </div>
            ))}
        </div>

        <button
          onClick={() => { setSectionFilter(''); setOpenSection(isOpen ? null : 'trackers'); }}
          className="tracker-summary__table-btn"
        >
          {isOpen ? 'Hide full table' : 'View full tracker table'}
        </button>

        {isOpen && (
          <div className="tracker-summary__table-wrapper">
            <TrackerTable
              trackers={trackers}
              initialFilter={openSection === 'trackers' ? sectionFilter : ''}
              key={`trackers-${openSection === 'trackers' ? sectionFilter : ''}`}
            />
          </div>
        )}
      </div>
    );
  };

  const renderCookieReport = () => {
    const report = result.cookieReport;
    if (!report || report.summary.total === 0) return null;

    const isOpen = openSection === 'cookies';

    return (
      <div id="appendix-cookies" style={{ marginBottom: 32 }}>
        <div className="report-section-header">
          <span className="report-section-header__num">04</span>
          <h2 className="report-section-header__title">Cookie Report</h2>
          <span className="report-section-header__meta">{report.summary.total} cookie{report.summary.total === 1 ? '' : 's'}</span>
        </div>

        {/* Why these counts can differ from the tracker section's (#1409). */}
        {report.note && (
          <p style={{ margin: '0 0 12px', fontSize: 13, color: T.gray500 }}>{report.note}</p>
        )}

        <div className="tracker-summary__chips">
          {[
            { label: 'Strictly Necessary', val: report.summary.strictlyNecessary, color: T.success },
            { label: 'Marketing', val: report.summary.marketing, color: T.error },
            { label: 'Analytics', val: report.summary.analytics, color: T.info },
            { label: 'Preferences', val: report.summary.preferences, color: T.violet600 },
            { label: 'Uncategorized', val: report.summary.uncategorized, color: T.gray500 },
          ]
            .filter((x) => x.val > 0)
            .map(({ label, val, color }) => (
              <div key={label} className="tracker-chip">
                <span className="tracker-chip__dot" style={{ backgroundColor: color }} />
                <span className="tracker-chip__value">{val}</span>
                <span className="tracker-chip__label">{label}</span>
              </div>
            ))}
        </div>

        <button
          onClick={() => { setSectionFilter(''); setOpenSection(isOpen ? null : 'cookies'); }}
          className="tracker-summary__table-btn"
        >
          {isOpen ? 'Hide full table' : 'View full cookie table'}
        </button>

        {isOpen && (
          <div className="tracker-summary__table-wrapper">
            <CookieTable
              cookies={report.cookies}
              initialFilter={openSection === 'cookies' ? sectionFilter : ''}
              key={`cookies-${openSection === 'cookies' ? sectionFilter : ''}`}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="proof-appendix">
      {renderTrackerSummary()}
      {renderCookieReport()}

      {/* Network Trace */}
      {result.networkTrace?.requests && result.networkTrace.requests.length > 0 && (
        <div id="appendix-network" className="scan-network-container-wrapper">
          <div className="report-section-header">
            <span className="report-section-header__num">05</span>
            <h2 className="report-section-header__title">Network Trace</h2>
          </div>
          <div className="scan-network-panel-box">
            <NetworkTrace
              requests={result.networkTrace.requests}
              initialFilter={openSection === 'network' ? sectionFilter : ''}
              key={`network-${openSection === 'network' ? sectionFilter : ''}`}
            />
          </div>
        </div>
      )}

      {/* Scan video */}
      {result.videoPath && (
        <div id="appendix-video" className="scan-video-wrapper">
          <div className="report-section-header">
            <span className="report-section-header__num">06</span>
            <h2 className="report-section-header__title">Scan Recording</h2>
          </div>
          <video
            controls
            className="scan-video-element"
            src={`${
              process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:4702'
            }${result.videoPath}`}
          />
        </div>
      )}

      <PagesAssessed
        result={result}
        reportId={reportId}
        openSection={openSection}
        onToggle={(section) => { setSectionFilter(''); setOpenSection(openSection === section ? null : section); }}
      />
    </div>
  );
});
ProofAppendix.displayName = 'ProofAppendix';

export default ProofAppendix;
